import { Component, computed, signal, effect, OnInit, OnDestroy, Input, HostListener, inject, Type } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { BillService } from '../../services/bill.service';
import { StorageService } from '../../services/storage.service';
import { CommonModule } from '@angular/common';
import { UploadZoneComponent } from '../upload-zone/upload-zone.component';
import { HierarchyItemComponent } from '../hierarchy/hierarchy-item.component';
import { BillListComponent } from '../bill-list/bill-list.component';
import { ColumnSelectorComponent } from '../column-selector/column-selector.component';
import { DuplicateResolverComponent } from '../duplicate-resolver/duplicate-resolver.component';
import { PairedBillResolverComponent } from '../paired-bill-resolver/paired-bill-resolver.component';
import { BillComparisonComponent } from '../bill-comparison/bill-comparison.component';
import { MergeModalComponent } from '../merge-modal/merge-modal.component';
import { SelectionActionBarComponent } from '../selection-action-bar/selection-action-bar.component';
import { ImportReviewComponent } from '../import-review/import-review.component';
import { BillAiFlowShellComponent } from '../bill-ai-flow/bill-ai-flow-shell/bill-ai-flow-shell.component';
import { BillPreviewComponent } from '../bill-preview/bill-preview.component';
import { DEFAULT_VISIBLE_COLUMNS, DEFAULT_BUDGETING_VISIBLE_COLUMNS } from '../../constants';
import { ColumnKey, BillFile, BillStatus, BillGroup, OnboardingContext, BillImportResponse } from '../../models';
import { ZenDialogMsgService } from '../../../zentility/_shared/_services/zen-dialog-msg.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SharedModule } from '../../../zentility/_shared/shared.module';
import { JenZenImgType } from '../../../zentility/_shared/_components/jen-zen-img/jen-zen-img.component';
import {
  OrganizationManagementService,
} from '../../../zentility/_shared/_zen-legacy-common/zen-common-services/_services/organization-management.service';
import { MatMenuModule } from '@angular/material/menu';
import { replacedBillsText } from '../bill-ai-flow/state/duplicate-resolution.util';
import { ZEN_GAMES, ZenGame } from '../../../zentility/_shared/_games/games.registry';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    UploadZoneComponent,
    HierarchyItemComponent,
    BillListComponent,
    ColumnSelectorComponent,
    DuplicateResolverComponent,
    PairedBillResolverComponent,
    BillComparisonComponent,
    MergeModalComponent,
    SelectionActionBarComponent,
    ImportReviewComponent,
    BillAiFlowShellComponent,
    BillPreviewComponent,
    MatTooltipModule,
    MatSlideToggleModule,
    MatMenuModule,
    SharedModule
],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit, OnDestroy {
    JenZenImgType = JenZenImgType;

    /**
     * Optional context for entity-level onboarding.
     * When provided, the dashboard operates in contextual mode showing only relevant fields.
     */
    @Input() context?: OnboardingContext;

    viewMode = signal<'hierarchy' | 'list'>('list');

    // UI State
    showColumnSelector = false;
    columnSelectorPosition: { top: string; right: string; maxHeight: string } = { top: '0', right: '0', maxHeight: '80vh' };
    showComparison = false;
    showImportReview = signal(false);
    showBillAiFlow = signal(false);
    /** Mini-game opt-in shown while budgeting bills crunch (the longer wait). */
    gameVisible = signal(false);
    /** Dismisses the "bills are ready" overlay so an absorbed player can keep playing. */
    doneNoticeAck = signal(false);
    /** Games the "Let's Play" dropdown offers (each lazy-loaded as its own chunk). */
    readonly games = ZEN_GAMES;
    /** The picked game's component, mounted lazily via NgComponentOutlet. */
    readonly activeGame = signal<Type<unknown> | null>(null);
    /** Id of the in-flight/active pick — guards against a rapid game-switch race. */
    readonly activeGameId = signal<string | null>(null);
    isLoadingSession = false;
    mergeModalState: { isOpen: boolean; type: 'entity' | 'address' | null; candidates: string[] } = {
        isOpen: false,
        type: null,
        candidates: []
    };

    // Signals from Service
    stats = this.billService.stats;
    isBudgetingMode = this.billService.isBudgetingMode;
    searchQuery = this.billService.searchQuery;
    processedBills = this.billService.processedBills;
    hierarchy = this.billService.hierarchy;
    selectedBillIds = this.billService.selectedBillIds;
    selectedEntityIds = this.billService.selectedEntityIds;
    selectedAddressIds = this.billService.selectedAddressIds;
    selectedMeterIds = this.billService.selectedMeterIds;
    reviewingPairedGroup = this.billService.reviewingPairedGroup;

    billRetriable = this.billService.billRetriable;

    hasFiles = computed(() => this.billService.getBills().length > 0);
    approvedBills = computed(() => this.billService.getBills().filter(b => b.status === BillStatus.APPROVED && b.data));
    isAllApproved = computed(() => this.stats().approvedCount === this.billService.getBills().length && this.billService.getBills().length > 0);
    isProcessing = computed(() => this.billService.getBills().some(b => b.status === BillStatus.PROCESSING));
    /** Bills that finished successfully and are importable (excludes errors, pending, processing, and unresolved duplicates) */
    successBillCount = computed(() =>
        this.billService.getBills().filter(b =>
            b.status !== BillStatus.ERROR &&
            b.status !== BillStatus.PENDING &&
            b.status !== BillStatus.PROCESSING &&
            b.status !== BillStatus.DUPLICATE_DETECTED
        ).length
    );
    errorBills = this.billService.errorBills;
    progress = computed(() => {
        const bills = this.billService.getBills();
        if (bills.length === 0) {
            return 0;
        }
        const processedCount = bills.filter(b => b.status !== BillStatus.PENDING && b.status !== BillStatus.PROCESSING).length;
        return (processedCount / bills.length) * 100;
    });

    // Computed for comparison
    comparisonBills = computed(() => this.billService.getSelectedBills());
    comparisonBillsPair = computed((): [BillFile, BillFile] | null => {
        const bills = this.comparisonBills();
        if (bills.length < 2) return null;
        return [bills[0], bills[1]];
    });

    visibleColumns = { ...DEFAULT_VISIBLE_COLUMNS };
    sortConfig: { key: ColumnKey; direction: 'asc' | 'desc' } | null = null;

    private progressInterval: any;

    // organizationSubject is the reliable source — the bare `.organization`
    // property is documented as not always set. inject() (not a constructor
    // param) so this.orgMgtSvc is set before the org field initializer runs.
    private orgMgtSvc = inject(OrganizationManagementService);
    private org = toSignal(this.orgMgtSvc.organizationSubject);
    /** Org-level budgeting entitlement — gates the Budgeting Mode toggle. */
    hasBudgetingFeature = computed(() => !!this.org()?.settings?.featureBudgeting);

    constructor(
        public billService: BillService,
        private storageService: StorageService,
        private zenDialogSvc: ZenDialogMsgService
    ) {
        // Watch for bill AI flow requests from child components
        effect(() => {
            if (this.billService.billAiFlowRequested()) {
                this.billService.clearBillAiFlowRequest();
                this.showBillAiFlow.set(true);
            }
        });
    }

    async ngOnInit() {
        // Set up default context if none provided (organization/portfolio level)
        if (!this.context) {
            this.context = {
                level: 'PORTFOLIO',
                storageKey: 'portfolio'
            };
        }

        // If BillService already has the right context loaded (e.g. session-manager
        // already called loadSessionWithContext), skip the redundant storage load.
        const currentCtx = this.billService.currentContext();
        if (currentCtx?.storageKey === this.context.storageKey) {
            // Context already active — just check if we should reopen bill AI flow
            if (this.billService.getBills().length > 0 && this.storageService.hasBillAiFlowState(this.context.storageKey)) {
                this.showBillAiFlow.set(true);
            }
            return;
        }

        // Clear stale in-memory state from a different context.
        // BillService is a root singleton — it survives component destruction,
        // so navigating between customers would otherwise show the old bills.
        if (currentCtx) {
            this.billService.clearBills();
        }

        // Try to load saved session — single loadSession call (no hasSession + loadSession double-parse)
        this.isLoadingSession = true;
        try {
            const session = await this.storageService.loadSession(this.context.storageKey);
            if (session && session.bills.length > 0) {
                await this.billService.loadSessionWithContext(session.context, session.bills, session.isBudgetingMode, session.rejectedPairings);

                // Auto-reopen bill AI flow if there's saved bill AI flow progress
                if (this.storageService.hasBillAiFlowState(this.context.storageKey)) {
                    this.showBillAiFlow.set(true);
                }
            } else {
                // No session, expired, or empty bills — just set context so new uploads work.
                this.billService.initContext(this.context);
            }
        } catch (err) {
            console.error('[Dashboard] Failed to restore session, initializing fresh context:', err);
            this.billService.initContext(this.context);
        } finally {
            this.isLoadingSession = false;
        }
    }

    ngOnDestroy() {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
        }
    }

    setViewMode(mode: 'hierarchy' | 'list') {
        this.viewMode.set(mode);
    }

    /** A new crunch begins — drop any prior game/overlay so the offer re-shows. */
    private resetGameState() {
        this.gameVisible.set(false);
        this.doneNoticeAck.set(false);
        this.activeGame.set(null);
        this.activeGameId.set(null);
    }

    onFilesSelected(files: File[]) {
        this.resetGameState();
        this.billService.addFiles(files);
    }

    handleFileInput(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            const files = Array.from(input.files).filter(f => f.type === 'application/pdf');
            if (files.length > 0) {
                this.resetGameState();
                this.billService.addFiles(files);
            } else {
                this.zenDialogSvc.openToast(false, 'Only PDF files are supported.');
            }
            input.value = ''; // Reset input
        }
    }
    
    @HostListener('document:keydown.escape')
    onEscapeKey() {
        if (this.showColumnSelector) {
            this.showColumnSelector = false;
        }
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    onDismissColumnSelector() {
        if (this.showColumnSelector) {
            this.showColumnSelector = false;
        }
    }

    toggleColumnSelector(trigger: HTMLElement) {
        if (this.showColumnSelector) {
            this.showColumnSelector = false;
            return;
        }
        const rect = trigger.getBoundingClientRect();
        const topPx = rect.bottom + 8;
        this.columnSelectorPosition = {
            top: `${topPx}px`,
            right: `${window.innerWidth - rect.right}px`,
            maxHeight: `${window.innerHeight - topPx - 16}px`
        };
        this.showColumnSelector = true;
    }

    toggleColumn(key: ColumnKey) {
        // Replace the reference so signal-input consumers (e.g. app-bill-list)
        // re-evaluate their `computed`s. Mutating in place wouldn't fire.
        this.visibleColumns = { ...this.visibleColumns, [key]: !this.visibleColumns[key] };
    }

    resetColumns() {
        const defaults = this.isBudgetingMode() ? DEFAULT_BUDGETING_VISIBLE_COLUMNS : DEFAULT_VISIBLE_COLUMNS;
        this.visibleColumns = { ...defaults };
    }

    // --- Merge/Compare Methods ---

    openMergeEntitiesModal() {
        const entityIds = this.selectedEntityIds();
        if (entityIds.length < 2) return;
        this.mergeModalState = {
            isOpen: true,
            type: 'entity',
            candidates: entityIds
        };
    }

    openMergeAddressesModal() {
        const addressIds = this.selectedAddressIds();
        if (addressIds.length < 2) return;
        this.mergeModalState = {
            isOpen: true,
            type: 'address',
            candidates: addressIds
        };
    }

    closeMergeModal() {
        this.mergeModalState = { ...this.mergeModalState, isOpen: false };
    }

    confirmMerge(targetId: string) {
        this.closeMergeModal();

        if (this.mergeModalState.type === 'entity') {
            this.billService.mergeEntities(this.mergeModalState.candidates, targetId);
        } else if (this.mergeModalState.type === 'address') {
            this.billService.mergeAddresses(this.mergeModalState.candidates, targetId);
        }
    }

    // --- Paired Bill Review Methods ---

    openPairReview(group: BillGroup) {
        this.billService.openPairedBillResolver(group);
    }

    deleteBills() {
        const billIds = this.selectedBillIds();
        if (billIds.length === 0) return;

        this.zenDialogSvc.showConfirmationDialog(
            'Delete Bills',
            `Are you sure you want to delete ${billIds.length} bill${billIds.length === 1 ? '' : 's'}?`,
            () => {}, // onCancel - do nothing
            () => {
                this.billService.deleteBills(billIds);
            },
            undefined, // afterClosed
            'Cancel',
            'Delete'
        );
    }

    deleteEntities() {
        const entityIds = this.selectedEntityIds();
        if (entityIds.length === 0) return;

        const entityText = entityIds.length === 1 ? 'this entity' : `these ${entityIds.length} entities`;

        // Count bills by checking which bills belong to the selected entities
        // Must use ALL bills, not just the filtered hierarchy
        const allBills = this.billService.getBills();
        let billCount = 0;

        allBills.forEach(bill => {
            const legalEntity = (bill.data?.legalEntity || 'Unknown Entity').trim();
            if (entityIds.includes(legalEntity)) {
                billCount++;
            }
        });

        const confirmMsg = `Are you sure you want to delete ${entityText}? This will delete ${billCount} bill${billCount === 1 ? '' : 's'}.`;

        this.zenDialogSvc.showConfirmationDialog(
            'Delete Entities',
            confirmMsg,
            () => {}, // onCancel - do nothing
            () => {
                this.billService.deleteEntities(entityIds);
            },
            undefined, // afterClosed
            'Cancel',
            'Delete'
        );
    }

    deleteAddresses() {
        const addressIds = this.selectedAddressIds();
        if (addressIds.length === 0) return;

        const addressText = addressIds.length === 1 ? 'this address' : `these ${addressIds.length} addresses`;

        // Count bills by checking which bills belong to the selected addresses
        // Must use ALL bills, not just the filtered hierarchy
        // Address ID format is: "Entity::Street::City"
        const allBills = this.billService.getBills();
        let billCount = 0;

        allBills.forEach(bill => {
            const legalEntity = (bill.data?.legalEntity || 'Unknown Entity').trim();
            const street = (bill.data?.serviceAddressStreet1 || 'Unknown Address').trim();
            const city = (bill.data?.serviceAddressCity || '').trim();
            const addressId = `${legalEntity}::${street}::${city}`;

            if (addressIds.includes(addressId)) {
                billCount++;
            }
        });

        const confirmMsg = `Are you sure you want to delete ${addressText}? This will delete ${billCount} bill${billCount === 1 ? '' : 's'}.`;

        this.zenDialogSvc.showConfirmationDialog(
            'Delete Addresses',
            confirmMsg,
            () => {}, // onCancel - do nothing
            () => {
                this.billService.deleteAddresses(addressIds);
            },
            undefined, // afterClosed
            'Cancel',
            'Delete'
        );
    }

    deleteMeters() {
        const meterIds = this.selectedMeterIds();
        if (meterIds.length === 0) return;

        const meterText = meterIds.length === 1 ? 'this meter' : `these ${meterIds.length} meters`;

        // Count bills using ALL bills (not the filtered hierarchy) to match actual deletion
        const billCount = this.billService.countBillsForMeters(meterIds);

        const confirmMsg = `Are you sure you want to delete ${meterText}? This will delete ${billCount} bill${billCount === 1 ? '' : 's'}.`;

        this.zenDialogSvc.showConfirmationDialog(
            'Delete Meters',
            confirmMsg,
            () => {},
            () => {
                this.billService.deleteMeters(meterIds);
            },
            undefined,
            'Cancel',
            'Delete'
        );
    }

    // --- Import Methods ---

    openImportReview() {
        this.showImportReview.set(true);
    }

    onImportComplete(response: BillImportResponse) {
        this.showImportReview.set(false);

        // Show success message
        const summary = response.summary;
        const replaced = replacedBillsText(summary.replacedBillCount);
        const message = `Successfully imported ${summary.billCount} bill(s)${replaced ? `, ${replaced}` : ''}`;
        this.zenDialogSvc.openToast(true, message);

        // Clear the bills since they are now imported
        this.billService.clearBills();
    }

    onImportCancelled() {
        this.showImportReview.set(false);
    }

    // --- Bill AI Flow Methods ---

    openBillAiFlow() {
        this.showBillAiFlow.set(true);
    }

    /** Pick a game from the dropdown — lazily loads its chunk, then shows the game panel. */
    playGame(game: ZenGame) {
        this.doneNoticeAck.set(false);
        this.gameVisible.set(true);
        this.activeGameId.set(game.id);
        game.load()
            .then(cmp => {
                // Ignore a resolved import if the panel was closed (Quit/done) or the user
                // switched to another game while this chunk was still in flight.
                if (this.gameVisible() && this.activeGameId() === game.id) {
                    this.activeGame.set(cmp);
                }
            })
            .catch(err => {
                // Chunk failed to load (often a stale chunk after a deploy) — drop back to
                // the offer and tell the user, rather than a blank panel with no explanation.
                if (this.activeGameId() === game.id) {
                    this.gameVisible.set(false);
                    this.activeGame.set(null);
                    this.activeGameId.set(null);
                }
                console.warn(`[Dashboard] Failed to load game "${game.id}":`, err);
                this.zenDialogSvc.openToast(false, 'Could not load the game. Please refresh and try again.');
            });
    }

    /** Leave the game — reverts to the offer (still crunching) or the ready bar (done). */
    quitGame() {
        this.gameVisible.set(false);
        this.activeGame.set(null);
        this.activeGameId.set(null);
    }

    /** Dismiss the "bills are ready" overlay but stay in the game. */
    keepPlaying() {
        this.doneNoticeAck.set(true);
    }

    onBillAiFlowComplete(response: BillImportResponse) {
        this.showBillAiFlow.set(false);

        const summary = response.summary;
        const replaced = replacedBillsText(summary.replacedBillCount);
        const message = summary.billCount === 0
            ? 'No bills imported: every bill was already imported'
            : `Successfully imported ${summary.billCount} bill(s)${replaced ? `, ${replaced}` : ''}`;
        this.zenDialogSvc.openToast(true, message);

        // NOTE: The shell already called clearBills() and clearPersistedState()
        // before emitting this event — do NOT call clearBills() again here because
        // the context is already null, which would skip the storage cleanup.
        // Re-set context so subsequent uploads on this same dashboard instance
        // have proper context for auto-save.
        if (this.context) {
            this.billService.initContext(this.context);
        }
    }

    onBillAiFlowCancelled() {
        this.showBillAiFlow.set(false);
    }
}